import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  FaSpinner,
  FaExclamationCircle,
  FaCheckCircle,
  FaCalendarAlt,
  FaArrowAltCircleUp,
  FaClock,
} from "react-icons/fa";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import useAxiosInstance from "../../hooks/useAxios";

const PROVIDER_PK = 9;
const MINIMAX_MODEL = "general";

const MiniMaxQuota = () => {
  const axiosInstance = useAxiosInstance();
  const axiosInstanceRef = useRef(axiosInstance);
  axiosInstanceRef.current = axiosInstance;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [apiKeys, setApiKeys] = useState({});
  const [quotaData, setQuotaData] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [chartWindow, setChartWindow] = useState("day"); // "day" | "week" | "month"

  const formatTimeRemaining = (ms) => {
    if (!ms || ms === Infinity) return "—";
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;

    return `${seconds}s`;
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return "bg-red-500";
    if (percentage >= 60) return "bg-orange-500";
    if (percentage >= 40) return "bg-yellow-500";
    return "bg-green-500";
  };

  // MiniMax now exposes only remaining_percent (integer) — count fields are unreliable.
  const extractQuotaPercents = (q) => {
    if (!q) return null;
    const i_rem = q.current_interval_remaining_percent;
    const w_rem = q.current_weekly_remaining_percent;
    return {
      interval_used_pct: typeof i_rem === "number" ? 100 - i_rem : null,
      weekly_used_pct: typeof w_rem === "number" ? 100 - w_rem : null,
      interval_remaining_pct: typeof i_rem === "number" ? i_rem : null,
      weekly_remaining_pct: typeof w_rem === "number" ? w_rem : null,
      remains_time: q.remains_time,
      weekly_remains_time: q.weekly_remains_time,
    };
  };

  const fetchPromptCounts = useCallback(async () => {
    try {
      const { data } = await axiosInstanceRef.current.get(
        "/assets/get_minimax_prompt_counts/",
      );
      setChartData(data?.data || null);
    } catch (error) {
      console.error("Failed to fetch prompt counts:", error);
      setChartData(null);
    }
  }, []);

  const fetchProviders = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axiosInstanceRef.current.get(
        "/wkl/llm/get_provider/?library_pk",
      );

      const providers = data?.data || data || [];

      const minimaxProvider = providers.find(({ pk }) => pk === PROVIDER_PK);

      const providerKeys =
        minimaxProvider?.additional_parameters?.api_keys || [];

      setApiKeys(providerKeys);

      if (providerKeys.length) {
        await fetchAllQuotasForKeys(providerKeys);
      }
    } catch (error) {
      console.error("Failed to fetch providers:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQuotaForKey = async ({
    api_key,
    seat_id,
    username,
    uid,
    active,
  }) => {
    try {
      const isTeamPlan = seat_id && seat_id.trim() !== "";
      const endpoint = isTeamPlan
        ? "https://platform.minimax.io/v1/api/openplatform/charge/token_plan/usage?page=1&page_size=100"
        : "https://www.minimax.io/v1/token_plan/remains";

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${api_key}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      let quota = null;
      let members = [];

      if (isTeamPlan) {
        members = data?.members || [];
        const minimaxQuotas = members.flatMap((member) =>
          (member.quotas || []).filter((q) => q.model_name === MINIMAX_MODEL),
        );
        if (minimaxQuotas.length > 0) {
          // Pool-level summary = average of member percentages
          const avg = (xs) =>
            xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
          quota = {
            interval_used_pct: avg(
              minimaxQuotas.map(
                (q) => 100 - (q.current_interval_remaining_percent ?? 100),
              ),
            ),
            weekly_used_pct: avg(
              minimaxQuotas.map(
                (q) => 100 - (q.current_weekly_remaining_percent ?? 100),
              ),
            ),
            interval_remaining_pct: avg(
              minimaxQuotas.map(
                (q) => q.current_interval_remaining_percent ?? 0,
              ),
            ),
            weekly_remaining_pct: avg(
              minimaxQuotas.map((q) => q.current_weekly_remaining_percent ?? 0),
            ),
            remains_time: Math.min(
              ...minimaxQuotas.map((q) => q.remains_time || Infinity),
            ),
            weekly_remains_time: Math.min(
              ...minimaxQuotas.map((q) => q.weekly_remains_time || Infinity),
            ),
          };
        }
      } else {
        // Individual plan response: model_remains array
        const hasNoSubscription = data?.base_resp?.status_code === 2062;
        const modelRemains = hasNoSubscription
          ? null
          : data?.model_remains?.find(
              ({ model_name }) => model_name === MINIMAX_MODEL,
            );
        quota = extractQuotaPercents(modelRemains);
        members = [
          {
            seat_id: seat_id || "",
            global_uid: uid || "",
            user_name: username || "Individual",
            combo_name: "Individual Plan",
            quotas: modelRemains ? [modelRemains] : [],
            noSubscription: hasNoSubscription,
            statusMsg: data?.base_resp?.status_msg,
          },
        ];
      }

      return {
        apiKey: `${api_key.slice(0, 12)}...`,
        rawKey: api_key,
        keyType: isTeamPlan ? "team" : "individual",
        username: username || "",
        seat_id: seat_id || "",
        uid: uid || "",
        active: active !== undefined ? active : true,
        data: quota,
        members: members.map((m) => {
          const minimaxQuota = (m.quotas || []).find(
            (q) => q.model_name === MINIMAX_MODEL,
          );
          return {
            seat_id: m.seat_id,
            global_uid: m.global_uid,
            user_name: m.user_name || username,
            combo_name: m.combo_name,
            data: extractQuotaPercents(minimaxQuota),
            noSubscription: m.noSubscription,
            statusMsg: m.statusMsg,
          };
        }),
        success: true,
      };
    } catch (error) {
      return {
        apiKey: `${api_key.slice(0, 12)}...`,
        rawKey: api_key,
        keyType: "unknown",
        username: username || "",
        seat_id: seat_id || "",
        uid: uid || "",
        active: active !== undefined ? active : true,
        error: error.message,
        success: false,
        members: [],
      };
    }
  };

  const fetchAllQuotasForKeys = useCallback(async (keys) => {
    if (!keys.length) return;

    try {
      setRefreshing(true);

      const seatIdToKeyInfo = {};
      keys.forEach((k) => {
        if (k.seat_id) {
          seatIdToKeyInfo[k.seat_id] = {
            active: k.active,
            username: k.username,
          };
        }
      });

      const teamKeys = keys.filter((k) => k.seat_id && k.seat_id.trim() !== "");
      const individualKeys = keys.filter(
        (k) => !k.seat_id || k.seat_id.trim() === "",
      );

      const teamKeyToFetch = teamKeys.length > 0 ? teamKeys[0] : null;
      const keysToFetch = teamKeyToFetch
        ? [teamKeyToFetch, ...individualKeys]
        : individualKeys;

      const results = await Promise.all(
        keysToFetch.map((k) => fetchQuotaForKey(k)),
      );

      const resultsMap = results.reduce((acc, result) => {
        const baseKey = result.uid || result.username || result.apiKey;

        if (result.keyType === "team") {
          result.members.forEach((m, idx) => {
            const mapKey = `team-${m.seat_id || m.global_uid || idx}`;
            const keyInfo = seatIdToKeyInfo[m.seat_id] || {};
            acc[mapKey] = {
              ...m,
              keyType: result.keyType,
              apiKey: result.apiKey,
              rawKey: result.rawKey,
              noSubscription: m.noSubscription,
              statusMsg: m.statusMsg,
              active:
                keyInfo.active !== undefined ? keyInfo.active : result.active,
              user_name: keyInfo.username || m.user_name || result.username,
            };
          });
        } else {
          const mapKey = result.uid ? `individual-${result.uid}` : baseKey;
          acc[mapKey] = {
            seat_id: result.seat_id,
            global_uid: result.uid,
            user_name: result.username,
            combo_name: "Individual Plan",
            data: result.data,
            keyType: result.keyType,
            apiKey: result.apiKey,
            rawKey: result.rawKey,
            noSubscription: result.members?.[0]?.noSubscription,
            statusMsg: result.members?.[0]?.statusMsg,
            error: result.error,
            success: result.success,
            active: result.active,
          };
        }
        return acc;
      }, {});

      setQuotaData(resultsMap);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch quotas:", error);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
    // Chart loads once on mount only; intentionally excluded from auto-refresh.
    fetchPromptCounts();
  }, [fetchProviders, fetchPromptCounts]);

  // Auto-refresh every 60 seconds — quota data only, not the chart.
  useEffect(() => {
    if (!apiKeys || apiKeys.length === 0) return;

    const interval = setInterval(() => {
      fetchAllQuotasForKeys(apiKeys);
    }, 60000);

    return () => clearInterval(interval);
  }, [apiKeys, fetchAllQuotasForKeys]);

  const overallStats = useMemo(() => {
    const allItems = Object.values(quotaData);
    const withData = allItems.filter((item) => item.data);

    const weeklyRemainPcts = withData
      .map((i) => i.data.weekly_remaining_pct)
      .filter((v) => typeof v === "number");
    const intervalRemainPcts = withData
      .map((i) => i.data.interval_remaining_pct)
      .filter((v) => typeof v === "number");
    const weeklyUsedPcts = withData
      .map((i) => i.data.weekly_used_pct)
      .filter((v) => typeof v === "number");

    const avg = (xs) =>
      xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
    const minTimeToReset = withData.reduce(
      (m, i) => Math.min(m, i.data.weekly_remains_time || Infinity),
      Infinity,
    );

    const avgWeeklyRemainingPct = avg(weeklyRemainPcts);
    const avgIntervalRemainingPct = avg(intervalRemainPcts);
    const avgWeeklyUsedPct = avg(weeklyUsedPcts);

    const totalWeekMs = 168 * 60 * 60 * 1000;
    const elapsedMs =
      totalWeekMs -
      (minTimeToReset === Infinity ? totalWeekMs : minTimeToReset);
    const elapsedFraction = elapsedMs / totalWeekMs;

    // Project the average used % to end of window. Only meaningful once ≥1% of week has elapsed.
    const estimatedWeeklyPct =
      elapsedFraction > 0.01 && avgWeeklyUsedPct != null
        ? Math.round(avgWeeklyUsedPct / elapsedFraction)
        : null;

    return {
      activeSeats: allItems.filter(
        (item) => item.active !== false && !item.noSubscription && item.data,
      ).length,
      totalSeats: allItems.length,
      noSubSeats: allItems.filter((item) => item.noSubscription).length,
      minTimeToReset,
      avgWeeklyRemainingPct,
      avgIntervalRemainingPct,
      estimatedWeeklyPct,
    };
  }, [quotaData]);

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-black">
            MiniMax Quota Monitor
          </h1>

          <p className="mt-1 text-sm text-gray-500">
            Track API key quotas and usage across intervals
          </p>
        </div>

        <button
          onClick={() => fetchAllQuotasForKeys(apiKeys)}
          disabled={refreshing || !apiKeys.length}
          className="flex items-center gap-2 px-4 py-2 text-white transition bg-black rounded-lg hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FaSpinner
            className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
          />

          {refreshing ? "Refreshing..." : "Refresh All"}
        </button>
      </div>

      {overallStats && (
        <div className="grid grid-cols-5 gap-4 mb-6">
          <StatCard
            icon={<FaCalendarAlt />}
            label="Weekly Reset In"
            value={formatTimeRemaining(overallStats.minTimeToReset)}
          />

          <StatCard
            icon={<FaArrowAltCircleUp />}
            label="Weekly Remaining"
            value={
              overallStats.avgWeeklyRemainingPct != null
                ? `${Math.round(overallStats.avgWeeklyRemainingPct)}%`
                : "—"
            }
            subValue="pool average"
          />

          <StatCard
            icon={<FaClock />}
            label="Interval Remaining"
            value={
              overallStats.avgIntervalRemainingPct != null
                ? `${Math.round(overallStats.avgIntervalRemainingPct)}%`
                : "—"
            }
            subValue="pool average"
          />

          <StatCard
            icon={<FaSpinner />}
            label="Est. Weekly Usage"
            value={
              overallStats.estimatedWeeklyPct != null
                ? `${overallStats.estimatedWeeklyPct}%`
                : "—"
            }
            subValue="projected"
          />

          <StatCard
            icon={<FaCheckCircle />}
            label="Active Keys"
            value={`${overallStats.activeSeats} / ${overallStats.totalSeats}`}
          />
        </div>
      )}

      <PromptVolumeChart
        chartData={chartData}
        chartWindow={chartWindow}
        setChartWindow={setChartWindow}
      />

      {/* {quotaData.some(item => item.keyType === 'sk-api' && item.members?.length > 0) && (
                <div className="p-3 mb-4 border rounded-lg bg-blue-50">
                    <p className="text-sm font-medium text-blue-800">
                        Total Team Members: {quotaData.reduce((sum, item) => sum + (item.members?.length || 0), 0)}
                    </p>
                </div>
            )} */}

      {lastUpdated && (
        <p className="mb-4 text-xs text-gray-400">
          Last updated: {lastUpdated.toLocaleString()}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {Object.values(quotaData).map((item, index) => {
          const weeklyUsedPct = item.data?.weekly_used_pct;
          const intervalUsedPct = item.data?.interval_used_pct;

          const displayName =
            item.user_name || item.global_uid || item.seat_id || "Unknown";
          const displayId =
            item.seat_id && item.seat_id.trim() !== ""
              ? item.seat_id
              : item.global_uid || "";

          const isInactive = item.active === false;

          return (
            <div
              key={`${item.global_uid || index}-${index}`}
              className={`rounded-lg border bg-white p-4 ${isInactive ? "opacity-60 border-red-200" : ""}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {isInactive ? (
                    <span className="relative flex w-3 h-3">
                      <span className="absolute inline-flex w-full h-full bg-red-400 rounded-full opacity-75"></span>
                      <span className="relative inline-flex w-3 h-3 bg-red-500 rounded-full"></span>
                    </span>
                  ) : item.data && !item.noSubscription ? (
                    <FaCheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <FaExclamationCircle className="w-5 h-5 text-red-500" />
                  )}

                  <span
                    className={`font-medium text-sm font-semibold ${isInactive ? "text-red-600" : ""}`}
                  >
                    {displayName}
                  </span>

                  {isInactive && (
                    <span className="text-xs font-normal text-red-500">
                      (Inactive)
                    </span>
                  )}
                </div>
              </div>

              {item.noSubscription ? (
                <div className="p-3 text-sm text-red-600 rounded bg-red-50">
                  {item.statusMsg || "No active token plan subscription"}
                </div>
              ) : item.data ? (
                <div className="space-y-4">
                  <QuotaProgress
                    label="Weekly Usage"
                    percentage={weeklyUsedPct}
                    color={getProgressColor(weeklyUsedPct ?? 0)}
                  />

                  <QuotaProgress
                    label="Interval Usage"
                    percentage={intervalUsedPct}
                    color={getProgressColor(intervalUsedPct ?? 0)}
                  />

                  <div className="grid grid-cols-2 gap-2 pt-3 text-xs border-t">
                    <div>
                      <p className="text-gray-400">Weekly Reset</p>

                      <p className="font-medium">
                        {formatTimeRemaining(item.data.weekly_remains_time)}
                      </p>
                    </div>

                    <div>
                      <p className="text-gray-400">Interval Reset</p>

                      <p className="font-medium">
                        {formatTimeRemaining(item.data.remains_time)}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-red-500">
                  Error: {item.error || "Failed to fetch quota"}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* {quotaData.some(item => item.members?.length > 0) && (
                <div className="p-4 mt-6 border border-blue-200 rounded-lg bg-blue-50">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-blue-800">
                            Team Members Details
                        </h3>
                        <span className="rounded bg-blue-200 px-2 py-0.5 text-xs font-medium text-blue-800">
                            {quotaData[0]?.members?.length || 0} seats
                        </span>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                        {quotaData[0]?.members?.map((member, mIndex) => {
                            const memberWeeklyPct = getUsagePercentage(
                                member.data?.current_weekly_usage_count,
                                member.data?.current_weekly_total_count
                            )
                            return (
                                <div key={mIndex} className="p-3 bg-white border border-blue-100 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-mono text-sm font-semibold text-gray-800">
                                            {member.user_name || `ID: ${member.global_uid}`}
                                        </span>
                                    </div>
                                    <div className="space-y-1.5">
                                        <div>
                                            <div className="flex justify-between text-xs text-gray-500">
                                                <span>Remaining</span>
                                                <span>{((member.data?.current_weekly_total_count || 0) - (member.data?.current_weekly_usage_count || 0)).toLocaleString()}</span>
                                            </div>
                                            <div className="mt-0.5 h-2 overflow-hidden rounded-full bg-gray-200">
                                                <div
                                                    className={`h-full ${getProgressColor(memberWeeklyPct)}`}
                                                    style={{ width: `${memberWeeklyPct}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )} */}

      {!loading && !refreshing && !apiKeys.length && (
        <div className="py-12 text-center rounded-lg bg-gray-50">
          <FaExclamationCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />

          <h3 className="mb-2 text-lg font-medium text-gray-900">
            No API Keys Found
          </h3>

          <p className="text-gray-500">
            No MiniMax API keys configured for provider pk=9
          </p>
        </div>
      )}

      {(loading || refreshing) && !quotaData.length && (
        <div className="flex items-center justify-center py-12">
          <FaSpinner className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon, label, value, subValue }) => (
  <div className="p-4 bg-white border rounded-lg">
    <div className="flex items-center gap-2 mb-2 text-sm text-gray-500">
      <span className="text-gray-500">{icon}</span>
      <span>{label}</span>
    </div>

    <p className="text-2xl font-bold">{value}</p>

    {subValue && <p className="text-xs text-gray-400">{subValue}</p>}
  </div>
);

const WINDOW_TABS = [
  { key: "day", label: "24h", desc: "hourly" },
  { key: "week", label: "7d", desc: "daily" },
];

const _parseBucketUTC = (bucket) => {
  // Backend sends naive ISO strings in UTC; append 'Z' so Date treats them as UTC.
  if (!bucket) return null;
  return new Date(bucket.endsWith("Z") ? bucket : `${bucket}Z`);
};

// Compact Y-axis number formatting: 1.2K, 100K, 1M, 1.5B, etc.
const _formatCompactNumber = (value) => {
  if (value == null || Number.isNaN(value)) return "";
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  const tiers = [
    { threshold: 1_000_000_000, suffix: "B" },
    { threshold: 1_000_000, suffix: "M" },
    { threshold: 1_000, suffix: "K" },
  ];
  for (const { threshold, suffix } of tiers) {
    if (abs >= threshold) {
      const v = abs / threshold;
      // Drop trailing .0 (e.g. "100K" not "100.0K") but keep "1.5K".
      const display = v >= 100 ? Math.round(v).toString() : v.toFixed(1).replace(/\.0$/, "");
      return `${sign}${display}${suffix}`;
    }
  }
  return `${sign}${abs}`;
};

const PromptVolumeChart = ({ chartData, chartWindow, setChartWindow }) => {
  const series = chartData?.[chartWindow] || [];
  const totals = series.reduce(
    (acc, p) => {
      acc.test += p.test || 0;
      acc.production += p.production || 0;
      acc.total += p.total || 0;
      return acc;
    },
    { test: 0, production: 0, total: 0 },
  );

  const isHourly = chartWindow === "day";
  const formatTick = (bucket) => {
    const d = _parseBucketUTC(bucket);
    if (!d) return "";
    if (isHourly) {
      return d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  };
  const formatTooltipLabel = (bucket) => {
    const d = _parseBucketUTC(bucket);
    if (!d) return bucket;
    return isHourly
      ? d.toLocaleString([], {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : d.toLocaleDateString([], {
          weekday: "short",
          month: "short",
          day: "numeric",
        });
  };

  return (
    <div className="p-4 mb-6 bg-white border rounded-lg">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            Minimax Prompt Volume
          </h3>
          <p className="text-xs text-gray-500">
            test + production · {totals.total.toLocaleString()} prompts in
            window
          </p>
        </div>

        <div className="flex gap-1 p-1 rounded-md bg-gray-100">
          {WINDOW_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setChartWindow(tab.key)}
              className={`px-3 py-1 text-xs font-medium rounded transition ${
                chartWindow === tab.key
                  ? "bg-white text-black shadow-sm"
                  : "text-gray-600 hover:text-black"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-64 w-full">
        {chartData == null ? (
          <div className="flex items-center justify-center h-full text-sm text-gray-400">
            <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
            Loading prompt volume...
          </div>
        ) : series.length === 0 ? (
          <div className="flex items-center justify-center h-full text-sm text-gray-400">
            No data in this window.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={series}
              margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="bucket"
                tick={{ fontSize: 11 }}
                stroke="#6b7280"
                tickFormatter={formatTick}
                minTickGap={20}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                stroke="#6b7280"
                allowDecimals={false}
                width={48}
                tickFormatter={_formatCompactNumber}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
                labelStyle={{ fontWeight: "bold" }}
                labelFormatter={formatTooltipLabel}
                formatter={(value, name) => [value.toLocaleString(), name]}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                iconType="circle"
              />
              <Line
                type="monotone"
                dataKey="production"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                name="Production"
              />
              <Line
                type="monotone"
                dataKey="test"
                stroke="#6366f1"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                name="Test"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-3 text-xs text-gray-500">
        <span>
          <span className="inline-block w-2 h-2 mr-1 align-middle bg-emerald-500 rounded-full" />
          Production: {totals.production.toLocaleString()}
        </span>
        <span>
          <span className="inline-block w-2 h-2 mr-1 align-middle bg-indigo-500 rounded-full" />
          Test: {totals.test.toLocaleString()}
        </span>
      </div>
    </div>
  );
};

const QuotaProgress = ({ label, percentage, color }) => {
  const pct = typeof percentage === "number" ? percentage : null;
  const display = pct == null ? "—" : `${Math.round(pct)}%`;
  const barWidth = pct == null ? 0 : Math.max(0, Math.min(100, pct));

  return (
    <div>
      <div className="flex justify-between mb-1 text-xs">
        <span className="text-gray-500">{label}</span>
        <span className="font-medium">{display} used</span>
      </div>

      <div className="h-3 overflow-hidden bg-gray-200 rounded-full">
        <div className={`h-full ${color}`} style={{ width: `${barWidth}%` }} />
      </div>
    </div>
  );
};

export default MiniMaxQuota;
